import { Formik, FormikHelpers } from 'formik'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { SmartCheckbox } from 'src/components/formik/Checkbox'
import { SmartInput } from 'src/components/formik/Input'
import { Button } from 'app/components/ui/Button'
import { FormFeedback } from 'app/components/ui/form/FormFeedback'
import { sendEmmaForm } from 'src/utils/sendEmmaForm'
import { FormGrid } from 'app/components/shared/FormGrid'
import { CompanyTypes } from 'EstraSuppliers'
import { FormSection } from 'app/components/shared/FormSection'
import { isValidFiscalCodeOrVat } from 'app/utils/formUtils'
import { FormGroup } from 'app/components/shared/FormGroup'
import { AuthenticationContext } from 'app/Contexts/AuthenticationProvider'

const companyTypes: {
  [key in CompanyTypes]: string
} = {
  individuale: 'Ditta individuale',
  snc: 'Snc',
  srl: 'Srl',
  sas: 'Sas',
  spa: 'Spa',
  sapa: 'Sapa',
  cooperativa: 'Cooperativa',
  scs: 'Scs',
  srlu: 'Srlu',
  ss: 'Ss',
  ati: 'Ati',
  ente: 'Ente',
  associazione: 'Associazione',
  fondazione: 'Fondazione',
  consorzio: 'Consorzio',
  onlus: 'Onlus',
  privato: 'Privato',
}

interface Props {
  onSubmitSuccess: () => void
}

const validationSchema = Yup.object().shape({
  companyName: Yup.string().required('Campo obbligatorio'),
  companyType: Yup.string().required('Campo obbligatorio'),
  companyFiscalCode: Yup.string()
    .test('is-valid-cf-or-vat', 'Formato non valido', value =>
      value ? isValidFiscalCodeOrVat(value) : true
    )
    .required('Campo obbligatorio'),
  companyVat: Yup.string().required('Campo obbligatorio'),
  email: Yup.string()
    .email('Formato non valido')
    .required('Campo obbligatorio'),
  firstName: Yup.string().required('Campo obbligatorio'),
  lastName: Yup.string().required('Campo obbligatorio'),
  mobileNumber: Yup.string()
    .matches(/^[0-9]*$/, `Numero non valido`)
    .required('Campo obbligatorio'),
  privacy: Yup.bool().oneOf([true], 'Il consenso alla privacy è obbligatorio'),
})

type OfferFormValues = {
  companyName: string
  companyType: string
  companyFiscalCode: string
  companyVat: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  mobileNumber: string
  privacy: boolean
}

export const OfferModalForm: React.FC<Props> = ({ onSubmitSuccess }) => {
  const authCtx = useContext(AuthenticationContext)

  // Get profile data from registration, used as default data for the form
  const supplierData = authCtx && authCtx.actor && authCtx.actor.supplier

  const initialValues: OfferFormValues = {
    companyName: supplierData?.company_name ?? '',
    companyType: supplierData?.company_type ?? '',
    companyFiscalCode: supplierData?.company_fiscal_code ?? '',
    companyVat: supplierData?.company_vat ?? '',
    email: authCtx?.actor?.email ?? '',
    firstName: supplierData?.holder_address?.name ?? '',
    lastName: supplierData?.holder_address?.surname ?? '',
    phoneNumber: supplierData?.phone ?? '',
    mobileNumber: supplierData?.company_mobile_number ?? '',
    privacy: false,
  }

  const [apiError, setApiError] = useState<string | null>()

  const handleSubmitError = (formikActions: FormikHelpers<OfferFormValues>) => {
    formikActions.setSubmitting(true)
    setApiError(
      'Qualcosa non sta funzionando come dovrebbe, ci scusiamo e ti richiediamo di riprovare.'
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        actions.setSubmitting(true)
        // post data
        sendEmmaForm({
          kind: 'supplier_contact',
          payload: values,
        })
          .then((res: { id: number }) => {
            if (res.id) {
              actions.resetForm()
              onSubmitSuccess()
            } else {
              handleSubmitError(actions)
            }
          })
          .catch(() => {
            handleSubmitError(actions)
          })
      }}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <FormSection title="Dati dell'azienda/professionista">
              <FormGrid cols={[6, 6]}>
                <SmartInput
                  label='Ragione sociale'
                  name='companyName'
                  required
                ></SmartInput>
                <SmartInput
                  label='Forma giuridica'
                  name='companyType'
                  type='select'
                  required
                  selectCaretSize='small'
                >
                  <option value={''}>Seleziona</option>
                  {Object.keys(companyTypes).map(optValue => (
                    <option key={optValue} value={optValue}>
                      {companyTypes[optValue as CompanyTypes]}
                    </option>
                  ))}
                </SmartInput>
              </FormGrid>

              <FormGrid cols={[6, 6]}>
                <SmartInput
                  label='Codice Fiscale'
                  name='companyFiscalCode'
                  required
                />
                <SmartInput label='Partita IVA' name='companyVat' required />
              </FormGrid>
            </FormSection>
            <FormSection title='Contatto per la proposta commerciale'>
              <FormGroup>
                <SmartInput type='email' name='email' label='Email' required />
              </FormGroup>
              <FormGrid cols={[6, 6]}>
                <SmartInput label='Nome' name='firstName' required />
                <SmartInput label='Cognome' name='lastName' required />
              </FormGrid>
              <FormGrid cols={[6, 6]}>
                <SmartInput
                  textPrefix='+39'
                  label='Telefono'
                  name='phoneNumber'
                />

                <SmartInput
                  textPrefix='+39'
                  label='Cellulare'
                  name='mobileNumber'
                  required
                />
              </FormGrid>
            </FormSection>
            <FormGroup>
              <SmartCheckbox name='privacy'>
                Dichiaro di aver preso visione dell&#39;
                <a
                  href='https://res.cloudinary.com/estra/image/upload/v1614592393/documenti/privacy-copyright/estra-energie-srl/Informativa_potenziali_ed_effettivi_EstraEnergie_2020.pdf'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  informativa privacy
                </a>{' '}
                e consento al trattamento dei miei dati per finalità di
                marketing diretto.
              </SmartCheckbox>
            </FormGroup>
            <ButtonWrapper>
              <Button
                type='submit'
                size='lg'
                disabled={isSubmitting}
                variant='nero'
              >
                Richiedi l&apos;offerta
              </Button>
              {apiError && (
                <FormFeedback variant='error'>{apiError}</FormFeedback>
              )}
            </ButtonWrapper>
          </Form>
        )
      }}
    </Formik>
  )
}

const Form = styled.form``

const ButtonWrapper = styled.div`
  text-align: center;
`
